import React, { useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, CircularProgress, FormControl, FormHelperText, Grid } from "@financeable-com-au/financeable-ui";
import { debounce } from "lodash";
import { PLAIN_TEXT_VEHICLE_SEARCH } from "@/constants";
import { NVIC } from "@/store/slices/types/applicationFormSlice";
import { PlainTextVehicleSearchProps } from "@/utils/interface";

/** Stable empty list, so an unsearched asset does not re-key the options memo. */
const NO_ROWS: NVIC[] = [];

interface VehicleOption {
  label: string;
  NVIC: string;
}

const toVehicleOptions = (rows: NVIC[]): VehicleOption[] =>
  rows.reduce<VehicleOption[]>((options, row) => {
    if (row?.ModelName && row?.NVIC_CUR) {
      options.push({ label: row.ModelName, NVIC: row.NVIC_CUR });
    }
    return options;
  }, []);

/** Free-text vehicle search; picking a result resolves the same specification the cascade would. */
const PlainTextVehicleSearch = ({ asset, handle, isDisableEdit }: PlainTextVehicleSearchProps) => {
  const {
    _id: assetId,
    plainTextSearchLoader = false,
    plainTextSearchList = NO_ROWS,
    plainTextSearchTerm,
    plainTextSearchError = false,
    searchVehicleLoader = false,
    nvicLoaderData = false,
  } = asset;

  const [query, setQuery] = useState("");

  // Once picked, the box holds the vehicle's name rather than a query, so no search is outstanding for it.
  const [isPicked, setIsPicked] = useState(false);

  // `handle` is rebuilt on every parent render; as a dependency the debounce timer would reset before firing.
  const searchRef = useRef(handle.searchVehiclesByKeyword);
  searchRef.current = handle.searchVehiclesByKeyword;

  /**
   * Remembers the term actually sent, so retyping a character costs nothing. A failed search stays
   * retryable: editing the term differs from this, and clearing resets it below.
   */
  const lastSearchedTerm = useRef<string | null>(null);

  const debouncedSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        if (keyword === lastSearchedTerm.current) return;

        lastSearchedTerm.current = keyword;
        searchRef.current(keyword);
      }, PLAIN_TEXT_VEHICLE_SEARCH.DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const term = query.trim();
  const isTermSearchable = term.length >= PLAIN_TEXT_VEHICLE_SEARCH.MIN_LENGTH;

  // Results outlive the query that fetched them; below the threshold they answer a term already gone.
  const options = useMemo(
    () => (isTermSearchable ? toVehicleOptions(plainTextSearchList) : []),
    [isTermSearchable, plainTextSearchList],
  );

  /** True once the store holds an answer for exactly this term. */
  const isSettled = !plainTextSearchLoader && plainTextSearchTerm === term;

  /** A search that failed has not established that the vehicle is absent, so it says so separately. */
  const emptyText = plainTextSearchError
    ? PLAIN_TEXT_VEHICLE_SEARCH.SEARCH_FAILED
    : PLAIN_TEXT_VEHICLE_SEARCH.NO_RESULTS;

  const helperText = () => {
    // The most specific state wins: a pick is settled, and its specification is on its way.
    if (nvicLoaderData) return PLAIN_TEXT_VEHICLE_SEARCH.RESOLVING;
    if (isPicked || !isTermSearchable) return PLAIN_TEXT_VEHICLE_SEARCH.HINT;
    if (!isSettled) return PLAIN_TEXT_VEHICLE_SEARCH.SEARCHING;
    if (options.length === 0) return emptyText;
    return PLAIN_TEXT_VEHICLE_SEARCH.HINT;
  };

  const handleInputChange = (_event: React.SyntheticEvent | null, value: string, reason: string) => {
    if (reason === "clear") {
      debouncedSearch.cancel();
      // Clearing the box starts a fresh search, so a term rejected or emptied last time is retryable.
      lastSearchedTerm.current = null;
      setQuery("");
      setIsPicked(false);
      return;
    }

    // "reset" fires when an option is picked; `handleSelect` owns the box's text from that point.
    if (reason !== "input") return;

    setQuery(value);
    setIsPicked(false);

    const nextTerm = value.trim();
    if (nextTerm.length < PLAIN_TEXT_VEHICLE_SEARCH.MIN_LENGTH) {
      debouncedSearch.cancel();
      return;
    }

    debouncedSearch(nextTerm);
  };

  const handleSelect = (_event: React.SyntheticEvent | null, option: VehicleOption | object | string | null) => {
    if (!option || typeof option === "string") return;

    const { label, NVIC } = option as VehicleOption;
    debouncedSearch.cancel();
    setQuery(label);
    setIsPicked(true);

    // The asset picks up the vehicle the same way it does from the cascade's "Full model name" field.
    handle.applyVehicleSelection({ NVIC, label });
  };

  return (
    // Matches the sibling options' spaced Grid, so all three options' fields share a left edge.
    <Grid container spacing={1}>
      <Grid xs={12}>
        {/* The id belongs on the FormControl: Joy derives the input's id from it and ties the helper text to it. */}
        <FormControl
          id={`plain-text-vehicle-search-${assetId}`}
          size="sm"
          disabled={isDisableEdit || searchVehicleLoader}
        >
          <Autocomplete
            size="md"
            placeholder={PLAIN_TEXT_VEHICLE_SEARCH.PLACEHOLDER}
            // The option's heading names the field on screen, so the name only has to reach screen readers.
            slotProps={{ input: { "aria-label": PLAIN_TEXT_VEHICLE_SEARCH.LABEL } }}
            options={options}
            value={null}
            inputValue={query}
            onInputChange={handleInputChange}
            onChange={handleSelect}
            getOptionLabel={(option) => (typeof option === "string" ? option : (option as VehicleOption).label)}
            // Keyed by name, as Joy does by default, two vehicles sharing one left a row behind when the list changed.
            getOptionKey={(option) => (typeof option === "string" ? option : (option as VehicleOption).NVIC)}
            // The server already ranked these; filtering again drops good matches worded differently.
            filterOptions={(unfiltered) => unfiltered}
            loading={plainTextSearchLoader}
            loadingText={PLAIN_TEXT_VEHICLE_SEARCH.SEARCHING}
            noOptionsText={emptyText}
            startDecorator={plainTextSearchLoader || nvicLoaderData ? <CircularProgress size="sm" /> : <SearchIcon />}
          />
          <FormHelperText>{helperText()}</FormHelperText>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default PlainTextVehicleSearch;
